import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
} from "@mui/material";

import {
  Attribute,
  GetUserAttributesRequest,
} from "@safetyculture/s12-apis-connect-web/s12/users/userattribute/v1/attribute_pb";

import { getUserAttributes } from "../../../api/endpoints/user-field";
import { callOptionsHeaders } from "../../../api/helpers";
import { GrpcError, SnackbarContext } from "../../../context/snackbar-context";
import GenericDialog from "../../shared/generic-dialog";
import EmptyTableResults from "../../shared/table/empty-table-results";
import { UserAttributesLoader } from "./user-attributes-loader";
import { UserAttributesTableHeader } from "./user-attributes-table-header";
import { UserAttributesTableRow } from "./user-attributes-table-row";

type UserAttributesDialogProps = {
  open: boolean;
  setIsOpen: (isOpen: boolean) => void;
  userId: string;
  roleId: string;
  regionCode: string;
};

export const UserAttributesDialog = (props: UserAttributesDialogProps) => {
  const { open, setIsOpen, userId, roleId, regionCode } = props;
  const { showError } = useContext(SnackbarContext);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const fetchUserAttributes = useCallback(async () => {
    try {
      setIsLoading(true);
      const req = new GetUserAttributesRequest({
        userId,
        orgId: roleId,
      });
      const attributes = await getUserAttributes(
        req,
        callOptionsHeaders(regionCode, roleId),
      );
      if (attributes) {
        setAttributes(attributes);
      }
    } catch (e) {
      showError(e as GrpcError);
    } finally {
      setIsLoading(false);
    }
  }, [regionCode, roleId, userId, showError]);

  useEffect(() => {
    if (open) {
      fetchUserAttributes();
    }
  }, [fetchUserAttributes, open]);
  return (
    <GenericDialog
      title="User Attributes"
      width="md"
      hasAckBtn={true}
      acknowledgeBtnLabel="OK"
      open={open}
      handleAckBtnClick={() => {
        setIsOpen(false);
      }}
      handleDialogClose={() => {
        setIsOpen(false);
      }}
      data-testid="user-attributes-dialog"
    >
      {isLoading && <UserAttributesLoader />}
      {!isLoading && (
        <TableContainer
          style={{ maxHeight: "600px", overflow: "auto" }}
          component={Paper}
        >
          <Table>
            <TableHead>
              <UserAttributesTableHeader />
            </TableHead>
            <TableBody>
              {attributes.length === 0 && (
                <EmptyTableResults
                  data-testid="user-attributes-table-empty"
                  colSpan={5}
                  tableHeading="user attributes for this user"
                />
              )}
              {attributes.map((attribute) => (
                <UserAttributesTableRow
                  attribute={attribute}
                  roleId={roleId}
                  regionCode={regionCode}
                  key={attribute.fieldId}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </GenericDialog>
  );
};
